import React, { useEffect, useState } from "react";
import { Card, Form, FormGroup, Row, Col, Button } from "react-bootstrap";
import useOauth from "@utils/customHooks/useOauth";
import API from "@API";
import {
  removeDuplicates,
  formatUnprocessibleResponse,
  handleAlert,
} from "@utils/helperFunctions";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import useUnloadWarning from "@utils/customHooks/useUnloadWarning";
import { GenericToggle } from "@components/FormsV2/GenericToggle";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import FontAwesomeIconTooltipped from "@components/Buttons/FontAwesomeIconTooltipped/FontAwesomeIconTooltipped";
import { faCircleInfo } from "@fortawesome/pro-light-svg-icons";
import MarkdownTextAreaV2 from "@components/FormsV2/MarkdownTextAreaV2";

function ProjectConditions({ account_slug, project_id, account_id }) {
  const [authorizationCode] = useOauth();
  const apiHost = process.env.REACT_APP_SCOPESTACK_API_HOST;
  const [projectConditions, setProjectConditions] = useState([]);
  const [conditionsChanged, setConditionsChanged] = useState([]);
  const [projectStatus, setProjectStatus] = useState("");

  const [errorMessages, setErrorMessages] = useState([]);
  const [showFailAlert, setShowFailAlert] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isFormDirty, setFormDirty] = useState(false);
  const [pageTitle, setPageTitle] = useState("");

  useUnloadWarning(isFormDirty);

  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (authorizationCode !== "") {
      // Get project conditions
      API.Get(
        `${apiHost}/v2/projects/${project_id}?include=project-conditions`,
        authorizationCode
      )
        .then((res) => {
          const data = res.data.data;
          setProjectStatus(data.attributes.status);

          if (res.data.included) {
            let conditions = res.data.included.filter(
              (item) => item.type === "project-conditions"
            );
            setProjectConditions(
              conditions.map((c) => {
                c.attributes.skip = c.attributes.status != "active";
                return c;
              })
            );
            if (data.attributes["field-labels"].terms_and_conditions) {
              setPageTitle(
                data.attributes["field-labels"].terms_and_conditions
              );
            }
          }
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
          setLoadFailed(true);
          setErrorMessages(
            "Terms and conditions could not be loaded. Refresh the page to try again."
          );
          setShowFailAlert(true);
        });
    }
  }, [authorizationCode]);

  const setConditionSkip = (id, skip) => {
    setProjectConditions((prev) =>
      prev.map((c) =>
        c.id == id ? { ...c, attributes: { ...c.attributes, skip } } : c
      )
    );
  };

  const handleToggleCondition = (condition) => {
    const skip = !condition.attributes.skip;
    setConditionSkip(condition.id, skip);
    const action = skip ? "skips" : "restore-requests";
    API.Put(
      `${apiHost}/v2/project-conditions/${condition.id}/${action}`,
      {},
      authorizationCode
    )
      .then((res) => {
        if (res.status == 200) {
          setSuccessMessage("Your changes were saved!");
          setShowSuccessAlert(true);
        }
      })
      .catch((err) => {
        // Put the toggle back so it matches what the server still has.
        setConditionSkip(condition.id, !skip);
        setErrorMessages(formatUnprocessibleResponse(err, "condition"));
        setShowFailAlert(true);
      });
  };

  const getLabel = (condition) => {
    return (
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <label
          htmlFor={"editor-" + condition.id}
          className={"form-label"}
          style={{ paddingTop: "0.6em" }}
          id={"editor-label-" + condition.id}
          key={"editor-label-" + condition.id}
        >
          {condition.attributes.name}
        </label>
        <div style={{ width: "10em", display: "flex" }}>
          <GenericToggle
            // GenericToggle only reads initialValueOfIsOn on mount; keying on
            // skip remounts it when a failed toggle is reverted.
            key={`toggle-${condition.id}-${condition.attributes.skip}`}
            hasText={true}
            onText="Included"
            offText="Excluded"
            initialValueOfIsOn={!condition.attributes.skip}
            onToggle={() => handleToggleCondition(condition)}
          />
          <FontAwesomeIconTooltipped
            icon={faCircleInfo}
            style={{
              color: "#418172",
              fontSize: "16px",
              marginTop: "0.6em",
              marginLeft: "0.6em",
            }}
            className={"pull-right"}
            toolTipText={
              condition.attributes.skip
                ? "You can toggle this on to include this condition in the generated document. It is currently excluded."
                : "You can toggle this off to exclude this condition from the generated document."
            }
          />
        </div>
      </div>
    );
  };

  const renderProjectConditions = () => {
    return projectConditions.map((condition) => {
      const disabled = condition.attributes.skip;
      return (
        <Row key={condition.id}>
          <Col style={{ paddingLeft: "2em", paddingRight: "2em" }}>
            <FormGroup>
              {getLabel(condition)}
              <MarkdownTextAreaV2
                key={"editor-" + condition.id}
                value={condition.attributes.content}
                placeholder=""
                alwaysEdit={!disabled}
                disabled={disabled}
                onChange={(markdown) => {
                  setFormDirty(true);
                  setConditionsChanged((prev) => [
                    ...prev,
                    { id: condition.id, content: markdown },
                  ]);
                }}
              />
            </FormGroup>
          </Col>
        </Row>
      );
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let projectPromises = [];
    let conditions = removeDuplicates(conditionsChanged, (item) => item.id);
    if (conditions.length === 0) {
      setFormDirty(false);
    } else {
      conditions.forEach((condition) => {
        const conditionData = {
          data: {
            type: "project-conditions",
            id: condition.id,
            attributes: {
              content: condition.content,
            },
            relationships: {
              project: { data: { type: "projects", id: String(project_id) } },
            },
          },
        };

        projectPromises.push(
          API.Patch(
            `${apiHost}/v2/project-conditions/${condition.id}`,
            conditionData,
            authorizationCode
          )
        );
      });

      Promise.all(projectPromises)
        .then((res) => {
          setFormDirty(false);
          setConditionsChanged([]);
          setSuccessMessage(pageTitle + " updated");
          setShowSuccessAlert(true);
        })
        .catch((err) => {
          setErrorMessages(formatUnprocessibleResponse(err, "condition"));
          setShowFailAlert(true);
        });
    }
  };

  const handleFormChange = (event) => {
    setFormDirty(true);
  };

  return (
    <div>
      {handleAlert(
        showFailAlert,
        errorMessages,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      <Card className="whiteBg">
        <Card.Body>
          <Card.Header className="cardHeaderOverride">
            <div className="headerContainer">
              <div className="actionBtns">
                <h2>
                  <span className="text24">{pageTitle}</span>
                </h2>
              </div>
              {projectStatus === "building" && (
                <div className="actionBtns">
                  <Button
                    className="btnSeafoam"
                    type="button"
                    onClick={(e) => {
                      handleSubmit(e);
                    }}
                  >
                    Save
                  </Button>
                </div>
              )}
            </div>
          </Card.Header>
          {isLoading ? (
            <ScopeStackSpinner />
          ) : loadFailed ? (
            <p>Terms and conditions could not be loaded.</p>
          ) : (
            <Form onChange={handleFormChange}>{renderProjectConditions()}</Form>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default ProjectConditions;
